import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { PatientViewModel } from '@/models/patient';

import { AddressesCard } from './AddressesCard';
import { ContactPointsCard } from './ContactPointsCard';
import { EthnicityCard } from './EthnicityCard';
import { IdentifiersCard } from './IdentifiersCard';
import { PersonalInfoCard } from './PersonalInfoCard';
import { PhysicalChartCard } from './PhysicalChartCard';

interface DemographicsSectionProps {
  readonly patient: PatientViewModel;
}

/**
 * Demographics is the patient identity panel. Six collapsible cards —
 * Personal Info, Identifiers, Contact Information, Addresses, Ethnicity, and
 * Physical Chart — with three product-design choices over the proto:
 *
 *   1. Personal Info edits commit through an explicit Save button (dirty
 *      footer), not autosave-on-blur, so accidental clicks don't persist.
 *   2. Identifier / contact / address rows use **click-to-edit** with an
 *      explicit confirm (Check). Add buttons append a row already in
 *      edit mode so the user lands in the input.
 *   3. Destructive deletes go through a **confirm dialog** — accidental
 *      removal of an MRN or the only contact point is bad for safety.
 *
 * All cards share a `CollapsibleCard` shell + `ConfirmDeleteDialog`
 * (`./_shared`); they stay folder-private until a second consumer needs
 * them, per `shared/rules/shared-first.md`.
 */
export function DemographicsSection({ patient }: DemographicsSectionProps): ReactElement {
  const { t } = useTranslation();
  const { guid, demographics, identifiers } = patient;

  return (
    <section
      id="demographics"
      aria-labelledby="demographics-heading"
      className="scroll-mt-4 space-y-3"
    >
      <h2 id="demographics-heading" className="sr-only">
        {t('patient.demographics.sectionTitle')}
      </h2>
      <PersonalInfoCard guid={guid} demographics={demographics} />
      <IdentifiersCard guid={guid} identifiers={identifiers} />
      <ContactPointsCard guid={guid} demographics={demographics} />
      <AddressesCard guid={guid} demographics={demographics} />
      <EthnicityCard guid={guid} demographics={demographics} />
      <PhysicalChartCard guid={guid} demographics={demographics} />
    </section>
  );
}
